// components/PriceComparison.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Messages } from 'primereact/messages';
import { Divider } from 'primereact/divider';
import { Tag } from 'primereact/tag';
import { priceComparisonApi } from './api';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

const PriceComparison = () => {
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  
  const [itemTypes, setItemTypes] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [locationError, setLocationError] = useState(null);
  
  const messagesRef = useRef(null);

  // Fetch dropdown data and get location on component mount
  useEffect(() => {
    const initialize = async () => {
      await fetchDropdownData();
      await getCurrentLocation();
    };
    initialize();
  }, []);

  // Fetch dropdown data
  const fetchDropdownData = async () => {
    try {
      const [itemTypesRes, treatmentsRes] = await Promise.all([
        priceComparisonApi.getItemTypes(),
        priceComparisonApi.getTreatments(),
      ]);

      if (itemTypesRes.data.success) {
        setItemTypes(itemTypesRes.data.data);
      }
      if (treatmentsRes.data.success) {
        setTreatments(treatmentsRes.data.data);
      }
    } catch (err) {
      console.error('Error fetching dropdown data:', err);
      showError('Failed to load dropdown options');
    }
  };

  // Show error message
  const showError = (message) => {
    if (messagesRef.current) {
      messagesRef.current.clear();
      messagesRef.current.show([
        { severity: 'error', summary: 'Error', detail: message, life: 5000 }
      ]);
    }
  };

  // Show success message
  const showSuccess = (message) => {
    if (messagesRef.current) {
      messagesRef.current.clear();
      messagesRef.current.show([
        { severity: 'success', summary: 'Success', detail: message, life: 3000 }
      ]);
    }
  };

  // Get current location automatically
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      setIsLoadingLocation(false);
      showError('Geolocation is not supported by your browser. Please enable location services.');
      return;
    }

    setIsLoadingLocation(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude);
        setLongitude(position.coords.longitude);
        setIsLoadingLocation(false);
        showSuccess(`Location detected successfully (${position.coords.latitude.toFixed(4)}, ${position.coords.longitude.toFixed(4)})`);
      },
      (error) => {
        console.error('Error getting location:', error);
        let errorMessage = 'Unable to get your location. ';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Please allow location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Location request timed out. Please try again.';
            break;
          default:
            errorMessage += 'Please try again or enter location manually.';
        }
        
        setLocationError(errorMessage);
        setIsLoadingLocation(false);
        showError(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };

  // Retry location
  const retryLocation = () => {
    getCurrentLocation();
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Check if location is available
    if (!latitude || !longitude) {
      showError('Location not available. Please wait or retry location detection.');
      return;
    }

    if (!selectedItemType) {
      showError('Please select an item type');
      return;
    }

    if (!selectedTreatment) {
      showError('Please select a treatment');
      return;
    }

    setLoading(true);
    setSearchPerformed(true);

    try {
      // Get the actual values - handle both object and string cases
      const itemTypeValue = typeof selectedItemType === 'object' ? selectedItemType.value : selectedItemType;
      const treatmentValue = typeof selectedTreatment === 'object' ? selectedTreatment.value : selectedTreatment;

      const requestData = {
        Latitude: parseFloat(latitude),
        Longitude: parseFloat(longitude),
        ItemType: itemTypeValue,
        Treatment: treatmentValue,
      };

      const response = await priceComparisonApi.comparePrices(requestData);

      if (response.data.success) {
        setResults(response.data.data);
        showSuccess(`Found ${response.data.count} shops within 5km radius`);
      } else {
        showError(response.data.message || 'Failed to compare prices');
      }
    } catch (err) {
      console.error('Error comparing prices:', err);
      if (err.response) {
        if (err.response.status === 400) {
          const validationErrors = err.response.data.errors;
          let errorMessage = 'Validation errors: ';
          if (validationErrors) {
            Object.keys(validationErrors).forEach(key => {
              errorMessage += `${key}: ${validationErrors[key].join(', ')} `;
            });
            showError(errorMessage);
          } else {
            showError('Invalid request. Please check your inputs.');
          }
        } else {
          showError(`Error: ${err.response.status} - ${err.response.statusText}`);
        }
      } else {
        showError('An error occurred while comparing prices');
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setSelectedItemType(null);
    setSelectedTreatment(null);
    setResults([]);
    setSearchPerformed(false);
    if (messagesRef.current) {
      messagesRef.current.clear();
    }
  };

  // Format distance
  const formatDistance = (meters) => {
    if (meters < 1000) {
      return `${Math.round(meters)}m`;
    }
    return `${(meters / 1000).toFixed(1)}km`;
  };

  // Template for distance column
  const distanceBodyTemplate = (rowData) => {
    return <Tag value={formatDistance(rowData.distanceInMeters)} severity="info" />;
  };

  // Template for price column
  const priceBodyTemplate = (rowData) => {
    return <span className="font-bold text-green-600">₹ {rowData.treatmentPrice.toFixed(2)}</span>;
  };

  // Template for base price column
  const basePriceBodyTemplate = (rowData) => {
    return <span>₹ {rowData.basePrice.toFixed(2)}</span>;
  };

  // Mobile card template for each row
 // Mobile card template with gradient background
const mobileCardTemplate = (rowData) => {
  return (
    <div 
      className="p-3 border-round-lg mb-3 shadow-3 transition-all transition-duration-300 hover:shadow-5 hover:scale-102"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        border: '1px solid rgba(255,255,255,0.2)',
        backdropFilter: 'blur(10px)',
        color: 'white',
        cursor: 'pointer'
      }}
    >
      <div className="flex justify-content-between align-items-center mb-3">
        <div className="flex align-items-center gap-2">
          <i className="pi pi-store text-2xl" style={{ color: 'rgba(255,255,255,0.8)' }}></i>
          <h3 className="text-xl font-bold m-0" style={{ color: 'white', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
            {rowData.shopName}
          </h3>
        </div>
        <div className="flex align-items-center gap-2">
          <i className="pi pi-map-marker" style={{ color: 'rgba(255,255,255,0.8)' }}></i>
          <span className="font-semibold text-sm" style={{ color: 'rgba(255,255,255,0.9)' }}>
            {formatDistance(rowData.distanceInMeters)}
          </span>
        </div>
      </div>
      
      <div className="grid p-0 m-0">
        <div className="col-6 p-1">
          <div className="text-xs opacity-70" style={{ color: 'rgba(255,255,255,0.7)' }}>Category</div>
          <div className="font-medium text-sm">{rowData.category}</div>
        </div>
        <div className="col-6 p-1">
          <div className="text-xs opacity-70" style={{ color: 'rgba(255,255,255,0.7)' }}>Item Type</div>
          <div className="font-medium text-sm">{rowData.itemType}</div>
        </div>
        <div className="col-6 p-1">
          <div className="text-xs opacity-70" style={{ color: 'rgba(255,255,255,0.7)' }}>Treatment</div>
          <div className="font-medium text-sm">{rowData.treatment}</div>
        </div>
        <div className="col-6 p-1">
          <div className="text-xs opacity-70" style={{ color: 'rgba(255,255,255,0.7)' }}>Base Price</div>
          <div className="font-medium text-sm">
            <span style={{ 
              background: 'rgba(255,255,255,0.2)',
              padding: '2px 8px',
              borderRadius: '12px',
              display: 'inline-block'
            }}>
              ₹ {rowData.basePrice.toFixed(2)}
            </span>
          </div>
        </div>
        <div className="col-12 p-1 mt-2">
          <div className="text-xs opacity-70" style={{ color: 'rgba(255,255,255,0.7)' }}>Treatment Price</div>
          <div 
            className="font-bold text-xl mt-1"
            style={{
              background: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: 'none',
              display: 'inline-block'
            }}
          >
            ₹ {rowData.treatmentPrice.toFixed(2)}
          </div>
          <div style={{ 
            background: 'rgba(255,255,255,0.15)',
            height: '2px',
            width: '60px',
            marginTop: '4px',
            borderRadius: '2px'
          }}></div>
        </div>
      </div>
    </div>
  );
};

  // Location status template
  const locationStatusTemplate = () => {
    if (isLoadingLocation) {
      return (
        <div className="flex align-items-center gap-2">
          <i className="pi pi-spin pi-spinner text-blue-500"></i>
          <span>Detecting your location...</span>
        </div>
      );
    }

    if (latitude && longitude) {
      return (
        <div className="flex align-items-center gap-2">
          <i className="pi pi-check-circle text-green-500"></i>
          <span>
            Location detected: {latitude.toFixed(4)}, {longitude.toFixed(4)}
          </span>
        </div>
      );
    }

    return (
      <div className="flex align-items-center gap-2">
        <i className="pi pi-exclamation-triangle text-yellow-500"></i>
        <span>Location not available</span>
        <Button 
          label="Retry" 
          icon="pi pi-refresh" 
          className="p-button-sm p-button-outlined" 
          onClick={retryLocation}
        />
      </div>
    );
  };

  return (
    <div className="p-4">
      <Card title="Price Comparison" className="mb-4">
        <Messages ref={messagesRef} />
        
        <div className="grid">
          {/* Location Status */}
          <div className="col-12">
            <div className="field grid">
              <label className="col-12 md:col-2 font-bold">Location</label>
              <div className="col-12 md:col-10">
                <div className="p-2 bg-blue-50 border-round-lg">
                  {locationStatusTemplate()}
                </div>
                {locationError && (
                  <div className="mt-2 text-sm text-red-500">
                    <i className="pi pi-info-circle mr-1"></i>
                    {locationError}
                  </div>
                )}
              </div>
            </div>
          </div>

          <Divider />

          {/* Item Type and Treatment Section */}
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block font-bold mb-2">Item Type</label>
              <Dropdown
                value={selectedItemType}
                options={itemTypes}
                onChange={(e) => setSelectedItemType(e.value)}
                optionLabel="label"
                placeholder="Select Item Type"
                className="w-full"
                filter
                showClear
                filterBy="label"
              />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block font-bold mb-2">Treatment</label>
              <Dropdown
                value={selectedTreatment}
                options={treatments}
                onChange={(e) => setSelectedTreatment(e.value)}
                optionLabel="label"
                placeholder="Select Treatment"
                className="w-full"
                filter
                showClear
                filterBy="label"
              />
            </div>
          </div>

          <Divider />

          {/* Action Buttons */}
          <div className="col-12 flex gap-2">
            <Button
              label={loading ? 'Searching...' : 'Compare Prices'}
              icon="pi pi-search"
              className="flex-1"
              onClick={handleSubmit}
              loading={loading}
              disabled={isLoadingLocation || !latitude || !longitude}
            />
            <Button
              label="Reset"
              icon="pi pi-refresh"
              className="p-button-outlined"
              onClick={handleReset}
            />
          </div>
        </div>
      </Card>

      {/* Results Section with Mobile Optimization */}
      {searchPerformed && !loading && (
        <Card title={`Results (${results.length} shops found)`} className="mt-3">
          {results.length === 0 ? (
            <div className="flex flex-column align-items-center p-4">
              <i className="pi pi-inbox text-6xl text-gray-400 mb-3"></i>
              <p className="text-center text-gray-500">
                No shops found matching your criteria within 5km radius.
              </p>
              <p className="text-center text-gray-400 text-sm">
                Try adjusting your filters or location.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop View - Full Table */}
              <div className="hidden md:block">
                <DataTable
                  value={results}
                  paginator
                  rows={10}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  size="small"
                  className="p-datatable-sm"
                  responsiveLayout="scroll"
                  emptyMessage="No shops found"
                >
                  <Column field="shopName" header="Shop Name" sortable />
                  <Column field="category" header="Category" sortable />
                  <Column field="itemType" header="Item Type" sortable />
                  <Column field="treatment" header="Treatment" sortable />
                  <Column
                    field="basePrice"
                    header="Base Price"
                    body={basePriceBodyTemplate}
                    sortable
                  />
                  <Column
                    field="treatmentPrice"
                    header="Treatment Price"
                    body={priceBodyTemplate}
                    sortable
                  />
                  <Column
                    field="distanceInMeters"
                    header="Distance"
                    body={distanceBodyTemplate}
                    sortable
                  />
                </DataTable>
              </div>

              {/* Mobile View - Card Layout */}
              <div className="block md:hidden">
                <DataTable
                  value={results}
                  paginator
                  rows={5}
                  rowsPerPageOptions={[5, 10, 25]}
                  size="small"
                  className="p-datatable-sm"
                  responsiveLayout="scroll"
                  emptyMessage="No shops found"
                >
                  <Column body={mobileCardTemplate} />
                </DataTable>
              </div>
            </>
          )}
        </Card>
      )}

      {/* Loading Spinner */}
      {loading && (
        <div className="flex justify-content-center align-items-center p-5">
          <ProgressSpinner />
        </div>
      )}
    </div>
  );
};

export default PriceComparison;